import { readFile } from 'node:fs/promises';
import { join } from 'node:path';
import type { AuthState } from '@wix/cli-auth';
import { updateFiles } from '@wix/ambassador-velo-ide-v1-gridapp-filestructure/http';
import { GridAppLayout } from '@wix/ambassador-velo-ide-v1-gridapp-filestructure/types';
import { getSourceFolder } from '@wix/velo-github-layout-definitions';
import { watch } from 'chokidar';
import { debounceQueue } from '@wix/debounce-queue';
import pLimit from 'p-limit';
import normalizePath from 'normalize-path';
import { CliError, CliErrorCode } from '@wix/cli-error';
import { httpRequest } from '../http-request.js';
import type { ProjectModel } from '../model.js';
import type { Logger } from '../logger.js';
import type { SitesBiEvents } from '../bi.js';
import { catchFilesMaxSizeError } from './error-handlers/maxFileSizeErrorHandler.js';

type ChokidarEventParams = [
  'add' | 'addDir' | 'change' | 'unlink' | 'unlinkDir',
  string,
];

const WATCH_FS_EVENTS_DEBOUNCE_TIME = 1000;

const getSectionName = (path: string) => {
  if (path.startsWith('src/pages')) {
    return 'public';
  }
  if (path.startsWith('src/backend')) {
    return 'backend';
  }
  return 'other';
};

export function startGridAppUpdater(
  model: ProjectModel,
  gridAppId: string,
  opts: {
    onError: (e: CliError) => void;
    authState: AuthState;
  },
  logger: Logger,
  biEvents: SitesBiEvents
) {
  const { projectFolder } = model;
  const limit = pLimit(1);

  const watcher = watch(getSourceFolder(projectFolder), {
    disableGlobbing: true,
    cwd: projectFolder,
    ignoreInitial: true,
  })
    .on(
      'all',
      debounceQueue(async (events: ChokidarEventParams[]) => {
        await limit(async () => {
          const logSynced = logger.logSyncingLocalCode();
          const { added, changed, removed } =
            normalizeChokidarBatchedEvents(events);
          const addedArray = Array.from(added.values()).map((path) => ({
            path,
            type: 'create',
          }));
          const changedArray = Array.from(changed.values()).map((path) => ({
            path,
            type: 'update',
          }));
          const removedArray = Array.from(removed.values()).map((path) => ({
            path,
            type: 'delete',
          }));

          for (const val of [...addedArray, ...changedArray, ...removedArray]) {
            biEvents.file({
              model,
              action: val.type as 'create' | 'update' | 'delete',
              path: val.path,
              oldPath: '',
              sectionName: getSectionName(val.path),
            });
            if (val.type === 'update') {
              biEvents.codeChanged(model, val.path);
            }
          }

          try {
            await writeFiles({
              filesToUpdate: await Promise.all(
                addedArray.concat(changedArray).map(async (val) => ({
                  path: normalizePath(val.path),
                  content: await readFile(
                    join(projectFolder, val.path),
                    'utf8'
                  ),
                }))
              ),
              removedArray,
              gridAppId,
              authState: opts.authState,
            });

            const message = logSynced.success();
            biEvents.messageDisplayed({ message, model });
            biEvents.readyForReloadAfterCodeChanged(model);
          } catch (e) {
            logSynced.fail();
            const error = new CliError({
              code: CliErrorCode.GridAppFailedToUpdateFiles(),
              cause: e,
              info: {
                added,
                changed,
                removed,
              },
            });
            opts.onError(error);
          }
        });
      }, WATCH_FS_EVENTS_DEBOUNCE_TIME)
    )
    .on('error', (e) => {
      const error = new CliError({
        code: CliErrorCode.FailedToWatchFiles(),
        cause: e,
      });

      opts.onError(error);
    });

  return { close: () => watcher.close() };
}

function normalizeChokidarBatchedEvents(events: ChokidarEventParams[]) {
  return events.reduce(
    (result, [eventType, filePath]) => {
      switch (eventType) {
        case 'add': {
          result.added.add(filePath);
          break;
        }
        case 'change': {
          result.changed.add(filePath);
          break;
        }
        case 'unlink': {
          result.added.delete(filePath);
          result.changed.delete(filePath);
          result.removed.add(filePath);
          break;
        }
        case 'unlinkDir': {
          const filePathAsDir = filePath.endsWith('/')
            ? filePath
            : `${filePath}/`;
          for (const filePath of result.added) {
            if (filePath.startsWith(filePathAsDir)) {
              result.added.delete(filePath);
            }
          }
          for (const filePath of result.changed) {
            if (filePath.startsWith(filePathAsDir)) {
              result.changed.delete(filePath);
            }
          }
          result.removed.add(filePath);
          break;
        }
        case 'addDir': {
          break;
        }
      }
      return result;
    },
    {
      added: new Set<string>(),
      changed: new Set<string>(),
      removed: new Set<string>(),
    }
  );
}

async function writeFiles({
  filesToUpdate,
  removedArray,
  gridAppId,
  authState,
}: {
  filesToUpdate: Array<{ path: string; content: string }>;
  removedArray: Array<{ path: string; type: string }>;
  gridAppId: string;
  authState: AuthState;
}) {
  try {
    await httpRequest(
      {
        type: 'code',
        authState,
      },
      updateFiles({
        gridAppId,
        layout: GridAppLayout.GITHUB,
        filesToUpdate,
        ignoreForbiddenPaths: true,
        pathsToDelete: removedArray.map((val) => val.path),
      })
    );
  } catch (err) {
    catchFilesMaxSizeError(err, filesToUpdate);
    throw err;
  }
}
